import {
  format,
  parse,
  parseISO,
  isValid,
  add,
  startOfISOWeek,
  startOfMonth,
  isWithinInterval,
  isAfter,
  endOfMonth,
} from 'date-fns'
import { UTCDate } from '@date-fns/utc'

export const dateText = (
  date: string | Date,
  stringFormat: string | null = null
): string => {
  const dateObj = date instanceof Date ? date : parseISO(date)
  return format(dateObj, stringFormat ?? 'dd MMM yyyy')
}

// Calendar dates have no time. Parsing them as a UTC instant and then
// formatting in the viewer's timezone shifts the day backward for anyone
// west of UTC, so keep the yyyy-MM-dd portion and format that as a local date.
export const calendarDateText = (date?: string | null): string => {
  if (!date) return ''
  const dateOnly = date.split('T')[0] ?? ''
  const parsed = parse(dateOnly, 'yyyy-MM-dd', new Date())
  if (!isValid(parsed)) return ''
  return format(parsed, 'MMMM d, yyyy')
}

// `format` always renders in the viewer's timezone, so an event-local time
// has to be moved onto the event's own wall clock before formatting —
// otherwise a London 18:30 reads as 13:30 to an admin in New York while
// still being labelled "Europe/London".
export const dateTextInTimeZone = (
  date: string | Date,
  timeZone: string,
  stringFormat: string | null = null
): string => {
  const isoDateTime = date instanceof Date ? date.toISOString() : date
  return dateText(toZonedBasicISODateTime(isoDateTime, timeZone), stringFormat)
}

export const addDays = (date: string | UTCDate, daysToAdd: number): UTCDate => {
  const dateObj = date instanceof UTCDate ? date : new UTCDate(parseISO(date))
  return new UTCDate(add(dateObj, { days: daysToAdd }))
}

export const addMonths = (
  date: string | UTCDate,
  monthsToAdd: number
): UTCDate => {
  const dateObj = date instanceof UTCDate ? date : new UTCDate(parseISO(date))
  return new UTCDate(add(dateObj, { months: monthsToAdd }))
}

export const startOfLastWeek = (): UTCDate => {
  const startOfWeek = startOfISOWeek(new UTCDate())
  return addDays(startOfWeek, -7)
}

export const startOfMonthForDate = (date: UTCDate): UTCDate => {
  return new UTCDate(startOfMonth(date))
}

export const endOfMonthForDate = (date: UTCDate): UTCDate => {
  return new UTCDate(endOfMonth(date))
}

export const dateIsWithinRange = (
  date: string | Date,
  dateToCheck: string | Date,
  rangeInDays: number
): boolean => {
  const dateObj =
    date instanceof Date ? new UTCDate(date) : new UTCDate(parseISO(date))
  const dateToCheckObj =
    dateToCheck instanceof Date
      ? new UTCDate(dateToCheck)
      : new UTCDate(parseISO(dateToCheck))

  const start = addDays(dateObj, -rangeInDays)
  const end = addDays(dateObj, rangeInDays)

  return isWithinInterval(dateToCheckObj, { start, end })
}

export const dateIsLive = (
  dateToCheck: string | Date,
  startDate: string | Date,
  endDate: string | Date | null | undefined
): boolean => {
  const start = startDate instanceof Date ? startDate : parseISO(startDate)
  const dateToCheckObj =
    dateToCheck instanceof Date ? dateToCheck : parseISO(dateToCheck)

  if (endDate === undefined || endDate === null) {
    return isAfter(dateToCheckObj, start)
  } else {
    const end = endDate instanceof Date ? endDate : parseISO(endDate)
    return isWithinInterval(dateToCheckObj, { start, end })
  }
}

// Wall-clock parts of a UTC instant in the given IANA timezone
const wallClockParts = (date: Date, timeZone: string) => {
  const formatted = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const part = (type: string) =>
    Number(formatted.find((p) => p.type === type)?.value)
  return {
    year: part('year'),
    month: part('month'),
    day: part('day'),
    hour: part('hour'),
    minute: part('minute'),
  }
}

// '2021-01-01T18:00:00.000Z' + 'Europe/Berlin' -> '2021-01-01T19:00'
export const toZonedBasicISODateTime = (
  isoDateTime: string,
  timeZone: string
): string => {
  const { year, month, day, hour, minute } = wallClockParts(
    new Date(isoDateTime),
    timeZone
  )
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`
}

// '2021-01-01T19:00' interpreted as Europe/Berlin -> '2021-01-01T18:00:00.000Z'
export const fromZonedBasicISODateTime = (
  basicISODateTime: string,
  timeZone: string
): string => {
  const [datePart, timePart] = basicISODateTime.split('T')
  const [year, month, day] = datePart!.split('-').map(Number)
  const [hour, minute] = timePart!.split(':').map(Number)
  const asUTC = Date.UTC(year!, month! - 1, day!, hour!, minute!)

  // Two passes converge on the right instant across DST transitions
  let timestamp = asUTC
  for (let i = 0; i < 2; i++) {
    const wall = wallClockParts(new Date(timestamp), timeZone)
    const wallAsUTC = Date.UTC(
      wall.year,
      wall.month - 1,
      wall.day,
      wall.hour,
      wall.minute
    )
    timestamp += asUTC - wallAsUTC
  }
  return new Date(timestamp).toISOString()
}

// '2021-01-01T00:00:00.000Z' -> '2021-01-01T00:00'
export const toBasicISODateTime = (isoDateTime: string) => {
  const date = new Date(isoDateTime)
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  const hours = String(date.getUTCHours()).padStart(2, '0')
  const minutes = String(date.getUTCMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

// '2021-01-01T00:00' -> '2021-01-01T00:00:00.000Z'
export const toFullISODateTime = (basicISODateTime: string) => {
  return `${basicISODateTime}:00.000Z`
}
